import React from "react";
import { Sparkles } from "lucide-react";

import { StatusBadge } from "@/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import type { OutgoingIntakeAnswerSourceMetadata } from "../../../api/types/outgoingIntake";

type Props = {
  metadata?: OutgoingIntakeAnswerSourceMetadata | null;
};

/**
 * Marks an answer the client accepted from Casey rather than typed themselves.
 *
 * The reviewing attorney's question is always "can I trust this value", so the
 * tooltip carries the document, page, and quoted text behind it — enough to
 * confirm without opening the file.
 */
export function AssistantAnswerBadge({ metadata }: Props) {
  const filename = metadata?.document_filename?.trim();
  const reference = metadata?.reference_text?.trim();
  const page = metadata?.page;

  const summary = filename
    ? `Suggested from ${filename}${
        typeof page === "number" ? `, page ${page}` : ""
      }`
    : "Suggested from the client's conversation with Casey";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <StatusBadge
            tone="accent"
            aria-label={summary}
            title={summary}
            className="cursor-default"
          >
            <Sparkles aria-hidden />
            AI-Assisted
          </StatusBadge>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" sideOffset={6}>
          {summary}
          {reference ? ` — "${reference}"` : ""}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
